import type { Key, Selection, SortDescriptor } from "@react-types/shared";
import { X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";

import { DataTableMobileView, type MobileRowDescriptor } from "@/components/flexkit/tables/DataTableMobileView";
import {
    Cell,
    Column,
    Row,
    StyledTable,
    TableBody,
    TableHeader,
    type DataTableDensity,
} from "@/components/flexkit/tables/DataTablePrimitives";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useContainerWidth } from "@/hooks/use-container-width";
export { DropdownMenuItem as RowActionsItem } from "@/components/podkit/dropdown/DropDown";

// ============================================================================
// CellAction — wraps interactive content inside a cell to prevent
// clicks/keypresses from toggling row selection.
// ============================================================================

export const CellAction: FC<{ children: ReactNode }> = ({ children }) => (
    <span
        className="flex justify-end"
        data-tracking-id-none
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
    >
        {children}
    </span>
);

// ============================================================================
// Internal column IDs for auto-generated action columns
// ============================================================================

/** Column ID for the per-row kebab menu (row actions dropdown). */
const ACTIONS_COLUMN_ID = "__actions__";

/** Column ID for inline action buttons rendered directly in the row. */
const INLINE_ACTIONS_COLUMN_ID = "__inline_actions__";

// ============================================================================
// Action Bar Context
// ============================================================================

export type ActionBarContext = {
    /** Set of selected row keys */
    selectedKeys: Set<Key>;
    /** Number of selected items (convenience) */
    selectedCount: number;
    /** Clears all selections */
    clearSelection: () => void;
};

// ============================================================================
// Column Definition
// ============================================================================

export type ColumnDef<T> = {
    /** Unique column identifier */
    id: string;
    /** Column header content */
    header: string | ReactNode;
    /** Render function for cell content.
     *  Use this for per-cell markup (icons, badges, formatted text).
     *  For column-level layout (width, visibility, alignment), use `className`. */
    cell: (row: T) => ReactNode;
    /** Marks this column as a row header for screen readers */
    isRowHeader?: boolean;
    /** Whether this column supports sorting */
    allowsSorting?: boolean;
    /** CSS classes applied to the column's `<th>` and `<td>` cells.
     *  Use for column-level layout: width (`w-[200px]`), responsive visibility
     *  (`hidden lg:table-cell`), or text alignment (`text-right`).
     *  For per-cell content styling, use the `cell` render function instead. */
    className?: string;
};

// ============================================================================
// DataTable Props
// ============================================================================

export type DataTableProps<T> = {
    /** Column definitions */
    columns: ColumnDef<T>[];
    /** Data rows */
    data: T[];
    /** Stable row identity function */
    getRowId: (row: T) => Key;

    // Selection
    /** Selection mode: "none" (default), "single", or "multiple" */
    selectionMode?: "none" | "single" | "multiple";
    /** Controlled selected keys */
    selectedKeys?: Selection;
    /** Selection change handler */
    onSelectionChange?: (keys: Selection) => void;

    // Floating action bar
    /** Render prop for the floating action bar content. DataTable owns the chrome
     *  (container, positioning, animation, close button). The consumer provides
     *  the action buttons. Shown when ≥1 row is selected. */
    actionBar?: (context: ActionBarContext) => ReactNode;

    // Sorting
    /** Current sort descriptor. undefined = unsorted (API default). */
    sortDescriptor?: SortDescriptor;
    /** Sort change handler. Called with undefined when sort is cleared (tri-state: asc → desc → unsorted). */
    onSortChange?: (descriptor: SortDescriptor | undefined) => void;

    // Density
    /** Row density: "default" or "condensed" */
    density?: DataTableDensity;

    // Layout
    /** Sticky header that stays visible when scrolling */
    stickyHeader?: boolean;
    /** Full-bleed layout (no max-width constraint) */
    fullBleed?: boolean;

    // Per-row actions
    /** Handler called when a user performs an action on a row (Enter key or click). Receives the row key. */
    onRowAction?: (key: Key) => void;
    /** Render inline action buttons per row (visible directly in the row, not behind a menu) */
    inlineActions?: (row: T) => ReactNode;
    /** Render per-row dropdown menu items (rendered inside a kebab DropdownActions menu) */
    rowActions?: (row: T) => ReactNode;

    // Mobile
    /**
     * Custom mobile row renderer. Below the md breakpoint, the table switches to a list view.
     *
     * Return a MobileRowDescriptor ({ title, description?, details?, icon? }) for structured layout,
     * or raw ReactNode for full control. If omitted, an auto-generated layout is derived from column defs.
     */
    mobileRow?: (row: T) => MobileRowDescriptor | ReactNode;

    // States
    /** Content shown when `data` is empty (e.g. "No results" after filtering).
     *  For first-time/onboarding empty states, handle that outside DataTable
     *  with an early return before rendering the component. */
    emptyState?: ReactNode;
    /** Loading state — reduces opacity */
    isLoading?: boolean;

    // Accessibility
    /** Accessible label for the table */
    "aria-label": string;
    /** Plain text value for a row, used for typeahead search. Falls back to the stringified row ID. */
    getRowTextValue?: (row: T) => string;

    // Styling
    /** Additional CSS classes */
    className?: string;
};

// ============================================================================
// Floating Action Bar
// ============================================================================

const ANIMATION_DURATION_MS = 200;

const FloatingActionBar: FC<{
    visible: boolean;
    children: ReactNode;
    onClose: () => void;
}> = ({ visible, children, onClose }) => {
    // Keep the bar mounted during the exit animation, showing frozen content.
    const [shouldRender, setShouldRender] = useState(visible);
    const frozenChildrenRef = useRef<ReactNode>(children);

    // Freeze children when visible — don't update during exit.
    if (visible) {
        frozenChildrenRef.current = children;
    }

    useEffect(() => {
        if (visible) {
            setShouldRender(true);
            return;
        }
        // Delay unmount to let the exit animation play.
        const timer = setTimeout(() => setShouldRender(false), ANIMATION_DURATION_MS);
        return () => clearTimeout(timer);
    }, [visible]);

    if (!shouldRender) return null;

    // Uses inline rendering to stay within any enclosing Radix focus scope
    // (e.g. a dialog). `position: fixed` handles viewport positioning.
    return (
        <div
            role="toolbar"
            aria-label="Bulk actions"
            aria-live={visible ? "polite" : "off"}
            aria-hidden={!visible}
            className={cn(
                "fixed bottom-4 left-0 right-0 z-[60] mx-auto w-fit",
                "flex items-center gap-3 rounded-xl border border-border-base bg-surface-primary px-4 py-2.5 shadow-lg",
                visible
                    ? "animate-in fade-in-0 slide-in-from-bottom-4 duration-200"
                    : "animate-out fade-out-0 slide-out-to-bottom-4 duration-200 fill-mode-forwards",
            )}
        >
            <Tooltip
                usePortal
                content={
                    <span className="inline-flex items-center gap-1.5">
                        Clear selection <KeyboardKey>Esc</KeyboardKey>
                    </span>
                }
            >
                <button
                    type="button"
                    aria-label="Clear selection"
                    onClick={onClose}
                    data-tracking-id="datatable-action-bar-clear"
                    className="inline-flex size-6 items-center justify-center rounded-md text-content-secondary hover:bg-surface-button-clear-accent hover:text-content-primary"
                >
                    <X className="size-4" />
                </button>
            </Tooltip>
            <div className="h-5 w-px bg-border-base" />
            {frozenChildrenRef.current}
        </div>
    );
};

// ============================================================================
// DataTable Component
// ============================================================================

export function DataTable<T extends object>({
    columns,
    data,
    getRowId,
    selectionMode = "none",
    selectedKeys,
    onSelectionChange,
    actionBar,
    sortDescriptor,
    onSortChange,
    density = "default",
    stickyHeader = false,
    fullBleed = false,
    onRowAction,
    inlineActions,
    rowActions,
    mobileRow,
    emptyState,
    isLoading,
    "aria-label": ariaLabel,
    getRowTextValue,
    className,
}: DataTableProps<T>) {
    const [containerRef, containerWidth] = useContainerWidth();
    const isMobile = containerWidth > 0 && containerWidth < 768;

    // Refs keep the latest render functions without invalidating the memo
    const inlineActionsRef = useRef(inlineActions);
    inlineActionsRef.current = inlineActions;
    const rowActionsRef = useRef(rowActions);
    rowActionsRef.current = rowActions;

    // Add inline actions and kebab menu columns
    const allColumns = useMemo((): ColumnDef<T>[] => {
        const extra: ColumnDef<T>[] = [];
        if (inlineActionsRef.current) {
            extra.push({
                id: INLINE_ACTIONS_COLUMN_ID,
                header: "",
                cell: (row: T) => <CellAction>{inlineActionsRef.current!(row)}</CellAction>,
            });
        }
        if (rowActionsRef.current) {
            extra.push({
                id: ACTIONS_COLUMN_ID,
                header: "",
                cell: (row: T) => (
                    <CellAction>
                        <DropdownActions contentClassName="w-48" side="right" align="start">
                            {rowActionsRef.current!(row)}
                        </DropdownActions>
                    </CellAction>
                ),
            });
        }
        if (extra.length === 0) return columns;
        return [...columns, ...extra];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, !!inlineActions, !!rowActions]);

    // Tri-state sort: ascending → descending → unsorted.
    // react-aria only supports bi-state (asc ↔ desc), so we intercept the callback.
    // When the user clicks a column that's already descending, we clear the sort.
    const handleSortChange = useCallback(
        (descriptor: SortDescriptor) => {
            if (!onSortChange) return;

            const sameColumn = sortDescriptor?.column === descriptor.column;
            if (sameColumn && sortDescriptor?.direction === "descending") {
                // Third click: clear sort
                onSortChange(undefined);
            } else {
                onSortChange(descriptor);
            }
        },
        [onSortChange, sortDescriptor],
    );

    // Build column className map for the primitives layer
    const columnClassNames = useMemo(() => {
        const map: Record<string, string> = {};
        for (const col of allColumns) {
            if (col.className) {
                map[col.id] = col.className;
            }
        }
        return Object.keys(map).length > 0 ? map : undefined;
    }, [allColumns]);

    // Auto-generate mobileRow from column definitions when not provided
    const effectiveMobileRow = useMemo(() => {
        if (mobileRow) return mobileRow;

        // Auto-generate: first isRowHeader column is the title, rest are details
        return (row: T): MobileRowDescriptor => {
            const titleCol = columns.find((c) => c.isRowHeader) ?? columns[0];
            const detailCols = columns.filter((c) => c.id !== titleCol?.id && c.id !== ACTIONS_COLUMN_ID);

            return {
                title: titleCol ? titleCol.cell(row) : null,
                details: detailCols.map((col) => ({
                    label: typeof col.header === "string" ? col.header : col.id,
                    value: col.cell(row),
                })),
            };
        };
    }, [mobileRow, columns]);

    // Clear selection helper for the floating action bar and Esc key
    const clearSelection = useCallback(() => {
        onSelectionChange?.(new Set());
    }, [onSelectionChange]);

    // Resolve selected keys into a concrete Set for the action bar context.
    // When "all" is selected, build the set from data so consumers get real keys.
    const isSelectAll = selectedKeys === "all";
    const selectedKeysSet = useMemo((): Set<Key> => {
        if (isSelectAll) return new Set(data.map(getRowId));
        if (!selectedKeys) return new Set();
        return selectedKeys as Set<Key>;
    }, [selectedKeys, isSelectAll, data, getRowId]);

    const hasSelection = isSelectAll || selectedKeysSet.size > 0;

    // Esc key clears selection globally when the table (or its portal) has focus
    const containerRefCb = useRef<HTMLDivElement | null>(null);
    const setContainerRef = useCallback(
        (node: HTMLElement | null) => {
            containerRef(node);
            containerRefCb.current = node as HTMLDivElement | null;
        },
        [containerRef],
    );

    useEffect(() => {
        if (!hasSelection) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;

            // Don't intercept Esc when a menu, dialog, or popover is open —
            // let the overlay handle it first.
            const target = e.target as HTMLElement | null;
            if (
                target?.closest('[role="menu"], [role="dialog"], [role="listbox"], [data-radix-popper-content-wrapper]')
            ) {
                return;
            }

            e.preventDefault();
            clearSelection();
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [hasSelection, clearSelection]);

    // Wrap in @container so column classNames can use container query variants
    // (@md:table-cell, @lg:hidden, etc.) instead of viewport-based variants.
    // The mobile/desktop switch is driven by the container's own width.
    return (
        <div ref={setContainerRef} className="@container">
            {isMobile ? (
                <DataTableMobileView
                    data={data}
                    getRowId={getRowId}
                    mobileRow={effectiveMobileRow}
                    inlineActions={inlineActions}
                    rowActions={rowActions}
                    selectionMode={selectionMode}
                    selectedKeys={selectedKeys}
                    onSelectionChange={onSelectionChange}
                    hasSelection={hasSelection}
                    density={density}
                    emptyState={emptyState}
                    isLoading={isLoading}
                    aria-label={ariaLabel}
                    className={className}
                />
            ) : (
                <StyledTable
                    aria-label={ariaLabel}
                    selectionMode={selectionMode === "none" ? undefined : selectionMode}
                    selectedKeys={selectedKeys}
                    onSelectionChange={onSelectionChange}
                    sortDescriptor={sortDescriptor}
                    onSortChange={handleSortChange}
                    onRowAction={onRowAction}
                    hasSelection={hasSelection}
                    density={density}
                    stickyHeader={stickyHeader}
                    fullBleed={fullBleed}
                    columnClassNames={columnClassNames}
                    isLoading={isLoading}
                    emptyState={emptyState}
                    className={className}
                >
                    <TableHeader>
                        {allColumns.map((col) => (
                            <Column key={col.id} isRowHeader={col.isRowHeader} allowsSorting={col.allowsSorting}>
                                {col.header}
                            </Column>
                        ))}
                    </TableHeader>
                    <TableBody items={data}>
                        {(item) => (
                            <Row
                                key={getRowId(item)}
                                textValue={getRowTextValue ? getRowTextValue(item) : String(getRowId(item))}
                            >
                                {allColumns.map((col) => (
                                    <Cell key={`${getRowId(item)}:${col.id}`}>{col.cell(item)}</Cell>
                                ))}
                            </Row>
                        )}
                    </TableBody>
                </StyledTable>
            )}
            {actionBar && (
                <FloatingActionBar visible={hasSelection} onClose={clearSelection}>
                    {actionBar({
                        selectedKeys: selectedKeysSet,
                        selectedCount: selectedKeysSet.size,
                        clearSelection,
                    })}
                </FloatingActionBar>
            )}
        </div>
    );
}
